
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, TrendingUp } from "lucide-react";

interface TaskProgressProps {
  totalTasks: number;
  completedTasks: number;
}

const TaskProgress = ({ totalTasks, completedTasks }: TaskProgressProps) => {
  const completionPercentage = totalTasks > 0 
    ? Math.round((completedTasks / totalTasks) * 100) 
    : 0;
    
  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return "bg-gradient-success";
    if (percentage >= 60) return "bg-gradient-warning"; 
    return "bg-gradient-primary";
  };

  const getProgressMessage = (percentage: number) => {
    if (percentage === 100) return "Amazing! All tasks completed! 🎉";
    if (percentage >= 80) return "Great progress! Almost there! 💪";
    if (percentage >= 50) return "Keep it up! You're halfway there! 🚀";
    if (percentage > 0) return "Good start! Let's build momentum! ⭐";
    return "Ready to tackle your tasks? Let's go! 🎯";
  };
    
  return (
    <Card className="p-6 mb-8 bg-gradient-to-r from-indigo-50/50 via-white/80 to-purple-50/50 border-indigo-100/50 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-primary rounded-xl shadow-soft">
            <CheckCircle className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Weekly Progress</h2>
            <p className="text-sm text-slate-600">{getProgressMessage(completionPercentage)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge 
            variant="outline" 
            className="font-bold bg-white/70 text-indigo-700 border-indigo-200 px-4 py-2 text-base"
          >
            {completionPercentage}%
          </Badge>
          {completionPercentage > 0 && (
            <TrendingUp className="h-5 w-5 text-green-500" />
          )}
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="relative">
          <Progress 
            value={completionPercentage} 
            className="h-3 bg-slate-200/50 rounded-full overflow-hidden"
            indicatorColor={getProgressColor(completionPercentage)}
          />
          {completionPercentage > 0 && (
            <div 
              className="absolute top-0 left-0 h-3 rounded-full animate-shimmer opacity-30"
              style={{ 
                width: `${completionPercentage}%`,
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
                backgroundSize: '200% 100%'
              }}
            />
          )}
        </div>
        
        <div className="flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <span className="text-slate-600 font-medium">
              {completedTasks} of {totalTasks} completed
            </span>
            {totalTasks > 0 && (
              <span className="text-slate-500">
                {totalTasks - completedTasks} remaining
              </span>
            )}
          </div>
          {completionPercentage === 100 && totalTasks > 0 && (
            <span className="text-green-600 font-bold animate-bounce-subtle">
              🎉 All Done!
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TaskProgress;
